import { extendTheme, withDefaultVariant } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools';

const stylesControls = {
  bg: 'white',
  borderRadius: '12px',
  border: '1px solid #D0D5DD',
  color: 'primaryDark',
  padding: '0 20px',
  _placeholder: {
    color: 'rgba(59, 72, 81, 0.6)',
  },
  _focus: {
    border: 'solid 2px #2F343D',
    backgroundColor: 'white',
    color: 'secondaryDark',
  },
  _invalid: {
    borderColor: 'error',
    backgroundColor: 'white',
  },
  _disabled: {
    opacity: 1,
    color: 'rgba(59, 72, 81, 0.4)',
    backgroundColor: '#E0E0E0',
    border: 'none',
    _hover: {
      border: 'none',
      backgroundColor: '#E0E0E0',
    },
  },
};

const stylesControlsForDarkMode = {
  color: 'white',
  backgroundColor: 'primaryDark',
  _placeholder: {
    color: 'rgba(221, 221, 221, 0.5)',
  },
  _hover: {
    border: 'solid 2px #2F343D',
    color: 'white',
    backgroundColor: 'primaryDark',
  },
  _focus: {
    border: 'solid 2px #2F343D',
    color: 'white',
    backgroundColor: 'primaryDark',
  },
  _invalid: {
    borderColor: 'error',
    color: 'white',
    backgroundColor: 'primaryDark',
  },
  _disabled: {
    opacity: 1,
    color: 'rgba(221, 221, 221, 0.4)',
    border: 'none',
    _hover: {
      border: 'none',
      color: 'rgba(221, 221, 221, 0.4)',
    },
  },
};

const stylesControlsForLightBg = {
  backgroundColor: 'rgba(221, 221, 221, 0.5)',
  _hover: {
    border: 'solid 2px #2F343D',
    backgroundColor: 'rgba(221, 221, 221, 0.5)',
    color: 'secondaryDark',
  },
};

const stylesControlsForDarkBg = {
  backgroundColor: 'white',
  _hover: { backgroundColor: '#FFFFFF', borderColor: '#2F343D' },
};

const sizesControls = {
  md: {
    field: {
      height: '50px',
      fontSize: '14px',
      lineHeight: '50px',
    },
  },
  sm: {
    field: {
      height: '36px',
      fontSize: '12px',
      lineHeight: '18px',
    },
  },
};

export const customTheme = extendTheme(
  {
    styles: {
      global: (props) => ({
        'html, body': {
          backgroundColor: mode('#F8F8F8', 'primaryDark')(props),
          color: mode('primaryDark', 'white')(props),
          height: '100%',
          scrollbarColor: '#a5a5a5 transparent' /* thumb and track color */,
          scrollbarWidth: 'thin',
        },
        'input:-webkit-autofill': {
          boxShadow: 'inset 0 0 0px 1000px #ECECEC',
          WebkitBoxShadow: 'inset 0 0 0px 1000px #ECECEC',
          borderColor: '#ECECEC',
        },
        'input:autofill': {
          boxShadow: 'inset 0 0 0px 1000px #ECECEC',
          WebkitBoxShadow: 'inset 0 0 0px 1000px #ECECEC',
          borderColor: '#ECECEC',
        },
        '.autofillForDarkBg input:-webkit-autofill': {
          boxShadow: 'inset 0 0 0px 1000px #FFFFFF',
          WebkitBoxShadow: 'inset 0 0 0px 1000px #FFFFFF',
          borderColor: '#FFFFFF',
          _hover: {
            borderColor: '#2F343D',
          },
        },
        '.autofillForDarkBg input:autofill': {
          boxShadow: 'inset 0 0 0px 1000px #FFFFFF',
          WebkitBoxShadow: 'inset 0 0 0px 1000px #FFFFFF',
          borderColor: '#FFFFFF',
          _hover: {
            borderColor: '#2F343D',
          },
        },
        '.chakra-ui-dark input:-webkit-autofill': {
          boxShadow: 'inset 0 0 0px 1000px #000000',
          WebkitBoxShadow: 'inset 0 0 0px 1000px #000000',
          borderColor: '#000000',
          WebkitTextFillColor: 'white !important',
          WebkitBackgroundClip: 'content-box',
        },
        '.chakra-ui-dark input:autofill': {
          boxShadow: 'inset 0 0 0px 1000px #000000',
          WebkitBoxShadow: 'inset 0 0 0px 1000px #000000',
          borderColor: '#000000',
          WebkitTextFillColor: 'white !important',
          WebkitBackgroundClip: 'content-box',
        },
        '.countryCodePrefixRightBorder': {
          '::after': {
            content: "''",
            position: 'absolute',
            height: '40%',
            width: '1.5px',
            top: '30%',
            right: '5px',
            backgroundColor: '#DDDDDD',
          },
        },
        '.androidGPayDetailsBorderHelper': {
          '::after': {
            content: "''",
            position: 'absolute',
            height: '15px',
            width: '1px',
            top: '-8px',
            right: '-1px',
            backgroundColor: 'primaryBackground',
          },
          '::before': {
            content: "''",
            position: 'absolute',
            height: '15px',
            width: '1px',
            top: '-8px',
            left: '-1px',
            backgroundColor: 'primaryBackground',
          },
        },
        '::-webkit-scrollbar': {
          width: '8px',
          background: 'lightgray',
        },
        '::-webkit-scrollbar-track': {
          WebkitBoxShadow: 'inset 0 0 6px rgb(195, 195, 195)',
          borderRadius: '15px',
        },
        '::-webkit-scrollbar-thumb': {
          borderRadius: '15px',
          WebkitBoxShadow: 'inset 0 0 6px rgba(219, 219, 219, 0.5)',
          background: '#a5a5a5',
        },
        '.primaryBoxShadow': {
          boxShadow: '0px 7px 23px rgba(0, 0, 0, 0.05)',
        },
        '.opacityEffectParent': {
          _hover: {
            '.opacityEffectEl': {
              opacity: 0,
            },
            '.opacityEffectElHover': {
              opacity: 1,
            },
          },
        },
        '.cutoutBorderGrey': {
          '::after': {
            content: "''",
            position: 'absolute',
            height: '7.2px',
            width: '90px',
            backgroundColor: mode('#f8f8f8', 'primaryDark')(props),
            top: '0px',
            left: '50%',
            transform: 'translateX(-50%)',
          },
        },
        '.cutoutBorderWhite': {
          '::after': {
            content: "''",
            position: 'absolute',
            height: '7px',
            width: '90px',
            backgroundColor: mode('white', 'secondaryDark')(props),
            top: '0px',
            left: '50%',
            transform: 'translateX(-50%)',
          },
        },
        '.orDivider': {
          '::before': {
            content: "''",
            position: 'absolute',
            height: '1px',
            width: '450px',
            backgroundColor: 'primaryBackground',
            top: '10px',
            bottom: '0px',
            left: '50%',
            transform: 'translateX(-50%)',
          },
        },
        '.sideNav': {
          a: {
            fontFamily: 'Inter Bold, sans-serif',
            fontSize: '13px',
            color: 'white',
            padding: '11px 14px',
            width: '100%',
            transition: '0.3s',
            _hover: {
              color: '#FF6D5D',
              '.multicolorElToDark': {
                fill: '#FF6D5D',
              },
              '.sideNavChevron': {
                fill: '#FF6D5D',
              },
              '.strokeBrandElToDark': {
                stroke: '#FF6D5D',
              },
            },
            '.strokeElToWhite': {
              color: 'secondaryDark',
            },
            '.strokeBrandElToDark': {
              color: '#FF6D5D',
            },
            '&.active': {
              color: 'white',
              bg: '#505E69',
              boxShadow: '0px 3.5px 5.5px rgba(0, 0, 0, 0.02)',
              '.sideNavSquare': { bg: 'gradientBrand' },
              '.multicolorElToWhite': {
                fill: 'white',
              },
              '.multicolorElToDark': {
                fill: '#FF6D5D',
              },
              '.sideNavChevron': {
                fill: '#FFFFFF',
              },
              '.strokeElToWhite': { stroke: 'white' },
              '.strokeBrandElToDark': { stroke: '#FF6D5D' },
              _hover: {
                '.sideNavChevron': {
                  fill: '#FFFFFF',
                },
              },
            },
            '.sideNavSquare': { bg: 'white', borderRadius: '12px' },
            '.sideNavIcon': { transition: '0.3s' },
          },
          '@media screen and (max-height: 780px)': {
            a: {
              padding: '9px 14px',
              fontSize: '12px',
            },
          },
          '@media screen and (max-height: 660px)': {
            a: {
              padding: '8px 12px',
              fontSize: '12px',
            },
          },
          '.needHelpSideBar': {
            fontFamily: 'Inter SemiBold, sans-serif',
            fontSize: '15px',
            color: '#4C4C4C',
            borderRadius: '12px',
            width: '100%',
            marginLeft: '5px',
            textDecoration: 'underline',
            textUnderlineOffset: '4px',
          },
          '.logoutSideBar': {
            fontFamily: 'Inter SemiBold, sans-serif',
            fontSize: '14px',
            color: mode('secondaryDark08', '#DDDDDD')(props),
            borderRadius: '12px',
            width: '100%',
            transition: '0.3s',
            _hover: {
              color: 'primaryDark',
            },
            '.sideNavSquare': { bg: 'white', borderRadius: '12px' },
            '.sideNavIcon': { transition: '0.3s' },
          },
        },
        '.chakra-ui-dark .sideNav': {
          a: {
            _hover: {
              color: '#FF6D5D',
            },
            '&.active': { bg: 'primaryDark', color: 'white' },
            '&.inactive': {
              '.sideNavSquare': { bg: 'primaryDark' },
              '.multicolorElToWhite': {
                fill: 'white',
              },
            },
          },
        },
        '.toolTipTriangleMyAcc': {
          position: 'absolute',
          display: 'block',
          top: '-2%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderBottom: '5px solid',
          borderBottomColor: mode('#fff', '#2D3748')(props),
        },
        '.toolTipTriangleClientDashboard': {
          position: 'absolute',
          display: 'block',
          top: '100%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderLeft: '15px solid transparent',
          borderRight: '15px solid transparent',
          borderTop: '15px solid',
          borderTopColor: mode('#2F343D', '#ddd')(props),
        },
        '.footerNav': {
          a: {
            _hover: {
              textDecoration: 'underline',
            },
            '&.active': {
              textDecoration: 'underline',
            },
          },
        },
        '.multicolorDarkBg': {
          '.darkBgElToWhite': {
            fill: 'white',
          },
          '.darkBgElToBrand': {
            fill: 'orangePrimary',
          },
        },
        '.reactColorfulParent': {
          _before: {
            content: '"#"',
            position: 'absolute',
            top: '5px',
            left: '60px',
            color: 'rgba(59, 72, 81, 0.8)',
            pointerEvents: 'none',
          },
        },
        '.chakra-ui-dark .reactColorfulParent': {
          _before: {
            color: '#DDDDDD',
          },
        },
        '.reactColorfulInput': {
          display: 'block',
          boxSizing: 'border-box',
          border: '2px solid transparent',
          borderRadius: '25px',
          background: '#fff',
          outline: 'none',
          font: 'inherit',
          textTransform: 'uppercase',
          textAlign: 'left',
          paddingLeft: '24px',
          fontSize: '14px',
          height: '32px',
          lineHeight: '32px',
          width: '106px',
          transition: '0.3s',
          _hover: {
            borderColor: '#2F343D',
          },
          _focus: {
            borderColor: '#2F343D',
          },
        },
        '.chakra-ui-dark .reactColorfulInput': {
          background: 'primaryDark',
        },
        '.publicHeading': {
          fontSize: '3.125rem !important',
          lineHeight: '4.06rem !important',
        },
        '.publicLightParagraph': {
          fontSize: '1.25rem',
          fontWeight: '350',
          lineHeight: '1.75rem',
          color: 'rgba(59, 72, 81, 0.8)',
        },
        '.publicParagraph': {
          fontSize: '1.625rem',
          fontWeight: '600',
          lineHeight: '2.25rem',
          color: 'rgba(59, 72, 81, 0.8)',
        },
        '.navbar-active': {
          borderBottom: '2px solid#FF4935',
        },
        '.animation-1': {
          opacity: '1 !important',
          scale: '1 !important',
        },
        '.animation-2': {
          opacity: '1 !important',
          left: '-10% !important',
        },
        '.animation-3': {
          opacity: '1 !important',
          left: '30% !important',
        },
        '.animation-4': {
          opacity: '1 !important',
          left: '-30% !important',
        },
        '.chakra-collapse': {
          overflow: 'initial !important',
        },
      }),
    },

    shadows: {
      cardHover: '0px 6px 24px 0px rgba(0, 0, 0, 0.12)',
    },

    semanticTokens: {
      colors: {
        //new colors
        orangePrimary: '#FF4935',
        orangePrimaryLight: 'rgba(255, 73, 53, 0.08)',
        jetBlack: '#252525',
        inkBlue: '#2F343D',
        softWhite: '#F8F8F8',
        subtleBackground: '#FAFAFA',
        lightGrey: '#D9D9D9',
        subtleBorder: '#E4E4E4',
        darkGrey: '#979797',
        dangerRed: '#FF0F0F',
        dangerRedLight: 'rgba(255, 15, 15, 0.08)',
        lightGrey2: '#C3C3C333',
        grey: '#989898',
        charcoalGrey: '#4C4C4C',
        warningOrange: '#FF5F15',
        warningOrangeLight: '#FF5F151A',
        warningYellow: '#EED202',
        savingsGreen: '#339900',
        recommendedGreen: '#34A853',
        recommendedGreenLight: 'rgba(52, 168, 83, 0.15)',
        lightestGrey: '#E4E4E4',
        borderGrey: '#C3C3C3',
        textGrey: '#465467',
        appleBlue: '#007AFF',
        googleGreen: '#34A754',
        sidebarBg: '#243744',
        subheadingGrey: '#AEAEAE',
        linkBlue: '#009BD6',
        alertRed: '#FF0000',
        messageStatusSuccessfulText: '#4D9825',
        messageStatusSuccessfulBackground: '#D9EDB9',
        messageStatusProcessingText: '#D8700D',
        messageStatusProcessingBackground: '#FFD09B',
        messageStatusScheduledText: '#D4A600',
        messageStatusScheduledBackground: '#FFF0AD',
        messageStatusPreparingText: '#4C70E6',
        messageStatusPreparingBackground: '#ADBDFA',
        messageStatusFailedText: '#EE241B',
        messageStatusFailedBackground: '#F8AAA6',
        messageStatusCancelledText: '#5C5C5C',
        messageStatusCancelledBackground: '#E0E0E0',
        messageStatusWarningText: '#D8700D',
        messageStatusWarningBackground: '#FFD09B',
        messageProgressTrack: '#F6D9D5',
        messageProgressValue: '#FF684A',

        //deprecated
        brand: {
          default: '#FF4935',
          _dark: '#FF4935',
        },
        brandDark: {
          default: '#EC6759',
          _dark: '#EC6759',
        },

        // 000000
        apiCodeRed: '#e94040',
        apiCodeBlue: '#008fff',
        androidGPayLabel: '#212121',
        androidGPayContent: '#616161',
        applePayLabel: '#8C8C8E',
        applePayUrl: '#1B76E0',
        primaryDark: '#000000',
        primaryDark06: 'rgba(41, 50, 58, 0.6)',
        primaryDark03: 'rgba(41, 50, 58, 0.3)',
        secondaryDark: { default: '#2F343D', _dark: '#2F343D' },
        secondaryDark08: 'rgba(59, 72, 81, 0.8)',
        secondaryDark06: 'rgba(59, 72, 81, 0.6)',
        error: { default: '#E53E3E', _dark: '#FF4935' },
        success: '#1DBE68',
        warning: '#FBC207',
        removed: '#6C747E',
        blueLight: '#0DB2F9',
        yellowLight: '#FFC759',
        primaryBackground: {
          default: '#DDDDDD',
          _dark: 'rgba(59, 72, 81, 0.8)',
        },
        secondaryBackground: '#EAEAEA',
        tertiaryBackground: '#FBFBFB',
        quaternaryBackground: '#F5F3F1',
        quinaryBackground: {
          default: '#EFEFEF',
          _dark: '#2F343D',
        },
        gradientOrange:
          'linear-gradient(180deg,rgba(255, 149, 84, 0.1) 16%, rgba(255, 73, 53, 0.1) 100%)',
        gradient:
          'linear-gradient(112.83deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.8) 110.84%)',
        gradientBrand:
          'linear-gradient(81.62deg,#FF4935 2.25%, #EC6759 79.87%)',
        gradientDark: 'linear-gradient(180deg, #000000 0%, #2F343D 100%);',

        onboardingBgGradient:
          'linear-gradient(135deg, #FF9554 0%, #FF4935 100%)',
        onboardingAccent: '#e85d3a',
        onboardingAmber: '#ffb060',
        onboardingPink: '#f24e7e',
      },
    },
    fonts: {
      heading: 'Gilroy Bold, sans-serif',
      body: 'Inter Regular, sans-serif',
    },
    layerStyles: {
      hoverCard: {
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
        _hover: { boxShadow: 'cardHover', transform: 'scale(1.02)' },
      },
    },
    textStyles: {
      //heading family
      headingFamilyBold: {
        fontFamily: 'Gilroy Bold, sans-serif',
      },
      headingFamilySemiBold: {
        fontFamily: 'Gilroy SemiBold, sans-serif',
      },
      headingFamilyMedium: {
        fontFamily: 'Gilroy Medium, sans-serif',
        lineHeight: '130%',
      },
      headingFamilyRegular: {
        fontFamily: 'Gilroy Regular, sans-serif',
      },

      //heading sizes
      heading1: {
        fontFamily: 'Gilroy Bold, sans-serif',
        fontSize: '64px',
        lineHeight: '130%',
      },
      heading2: {
        fontFamily: 'Gilroy SemiBold, sans-serif',
        fontSize: '48px',
        lineHeight: '130%',
      },
      heading3: {
        fontFamily: 'Gilroy Medium, sans-serif',
        fontSize: '32px',
        lineHeight: '130%',
      },
      heading4: {
        fontFamily: 'Gilroy Medium, sans-serif',
        fontSize: '24px',
        lineHeight: '130%',
      },
      heading5: {
        fontFamily: 'Gilroy Medium, sans-serif',
        fontSize: '20px',
        lineHeight: '130%',
      },
      sectionTitle: {
        fontFamily: 'Inter Bold, sans-serif',
        fontSize: '16px',
        textTransform: 'uppercase',
        letterSpacing: '0.16em',
      },

      //body family
      bodyFamilyBold: {
        fontFamily: 'Inter Bold, sans-serif',
      },
      bodyFamilySemiBold: {
        fontFamily: 'Inter SemiBold, sans-serif',
      },
      bodyFamilyMedium: {
        fontFamily: 'Inter Medium, sans-serif',
      },
      bodyFamilyRegular: {
        fontFamily: 'Inter Regular, sans-serif',
      },

      //body sizes
      bodyLarge: {
        fontFamily: 'Inter Regular, sans-serif',
        fontSize: '18px',
      },
      bodyMedium: {
        fontFamily: 'Inter Regular, sans-serif',
        fontSize: '16px',
        lineHeight: '140%',
      },
      bodySmall: {
        fontFamily: 'Inter Regular, sans-serif',
        fontSize: '14px',
        lineHeight: '140%',
      },

      //button text
      buttonText: {
        fontFamily: 'Inter Bold, sans-serif',
        fontSize: '14px',
      },

      //gradient text
      gradientText: {
        bgClip: 'text',
        bgGradient: 'linear(to-b, #FF9554, #FF4935)',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
      },

      //google pay
      headingFamilyRegularGooglePay: {
        fontFamily: "'Google Sans', sans-serif",
        fontWeight: 400,
        lineHeight: '130%',
      },
      headingFamilySemiBoldGooglePay: {
        fontFamily: "'Google Sans', sans-serif",
        fontWeight: 600,
        lineHeight: '130%',
      },

      //apple pay
      headingFamilyRegularApplePay: {
        fontFamily: 'Sf Pro Display Regular, sans-serif',
        lineHeight: '130%',
      },
      headingFamilyMediumApplePay: {
        fontFamily: 'Sf Pro Display Medium, sans-serif',
        lineHeight: '130%',
      },

      //pass download page
      passDownloadHeaderText: {
        fontFamily: 'Cera Pro Medium, sans-serif',
        fontSize: '24px',
        lineHeight: '32px',
      },
    },
    components: {
      Input: {
        variants: {
          new: (props) => ({
            field: {
              border: '1px solid #2F343D',
              borderRadius: '10px',
              padding: '10px',
            },
          }),
          filled: (props) => ({
            field:
              props.colorMode === 'dark'
                ? {
                    ...stylesControls,
                    ...stylesControlsForDarkMode,
                  }
                : {
                    ...stylesControls,
                    ...stylesControlsForLightBg,
                  },
          }),

          filledForDarkBg: (props) => ({
            field:
              props.colorMode === 'dark'
                ? {
                    ...stylesControls,
                    ...stylesControlsForDarkMode,
                  }
                : {
                    ...stylesControls,
                    ...stylesControlsForDarkBg,
                  },
          }),
          readOnlyUrl: (props) => ({
            field:
              props.colorMode === 'dark'
                ? {
                    ...stylesControls,
                    ...stylesControlsForDarkMode,
                    backgroundColor: 'primaryDark',
                    _hover: { backgroundColor: 'primaryDark' },
                    _focus: { backgroundColor: 'primaryDark' },
                    _active: { backgroundColor: 'primaryDark' },
                    cursor: 'not-allowed',
                  }
                : {
                    ...stylesControls,
                    ...stylesControlsForDarkBg,
                    bg: '#F8F8F8',
                    _hover: { bg: '#F8F8F8' },
                    _focus: { bg: '#F8F8F8' },
                    _active: { bg: '#F8F8F8' },
                    cursor: 'not-allowed',
                  },
          }),
          filledNoLeftBorder: (props) => ({
            field:
              props.colorMode === 'dark'
                ? {
                    ...stylesControls,
                    ...stylesControlsForDarkMode,
                  }
                : {
                    ...stylesControls,
                    ...stylesControlsForDarkBg,
                    border: '0px',
                    ':hover': {
                      border: '0px',
                    },
                    ':focus': {
                      border: '0px',
                    },
                  },
          }),
        },
        sizes: {
          ...sizesControls,
        },
      },
      Select: {
        variants: {
          filled: (props) => ({
            field:
              props.colorMode === 'dark'
                ? {
                    ...stylesControls,
                    ...stylesControlsForDarkMode,
                  }
                : {
                    ...stylesControls,
                    ...stylesControlsForLightBg,
                  },
          }),
          filledForDarkBg: (props) => ({
            field:
              props.colorMode === 'dark'
                ? {
                    ...stylesControls,
                    ...stylesControlsForDarkMode,
                  }
                : {
                    ...stylesControls,
                    ...stylesControlsForDarkBg,
                  },
          }),
          filledForDarkBg2: (props) => ({
            field:
              props.colorMode === 'dark'
                ? {
                    ...stylesControls,
                    ...stylesControlsForDarkMode,
                    backgroundColor: 'secondaryDark',
                  }
                : {
                    ...stylesControls,
                    ...stylesControlsForDarkBg,
                  },
          }),
          countryCode: {
            field: {
              borderRadius: '0px',
              backgroundColor: 'transparent',
            },
            icon: {
              color: '#dddd',
            },
          },
        },
        sizes: {
          ...sizesControls,
        },
      },
      Accordion: {
        variants: {
          docs: {
            container: {
              borderTopWidth: '0px',
              borderColor: 'transparent',
              _last: {
                borderBottomWidth: '0px',
              },
            },
            panel: {
              pt: 0,
              px: 0,
              pb: 0,
            },
            button: {
              width: 'fit-content',
              borderBottom: '2px solid transparent',
              px: 0,
              py: 1,
              _focus: {
                boxShadow: 'none',
              },
              _hover: {
                bg: 'none',
                borderColor: 'orangePrimary',
              },
            },
          },
          faq: {
            container: {
              mt: 4,
              borderTop: 'none',
              _last: {
                borderBottomWidth: '0px',
              },
            },
            panel: {
              p: 4,
              color: 'secondaryDark08',
            },
            button: {
              borderBottom: '1px solid #ddd',
              pl: '0',
              _focus: {
                boxShadow: 'none',
              },
              _hover: {
                bg: 'none',
              },
            },
          },
        },
      },
      Skeleton: {
        baseStyle: {
          borderRadius: '6px',
          opacity: 0.6,
        },
      },
      Popover: {
        baseStyle: {
          content: {
            border: 'none',
            borderRadius: '15px',
            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
            _focus: {
              // boxShadow: 'none',
              boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
      Textarea: {
        variants: {
          filled: (props) => ({
            backgroundColor: 'white',
            borderRadius: '12px',
            _placeholder: {
              color: 'rgba(59, 72, 81, 0.6)',
            },
            _hover: {
              backgroundColor: '#fff',
              border: 'solid 2px #2F343D',
            },
            _focus: {
              backgroundColor: '#fff',
              border: 'solid 2px #2F343D',
            },
            paddingTop: '10px !important',
            lineHeight: '140%',
            fontSize: '14px',
          }),
          outlined: (props) => ({
            backgroundColor: '#EEEEEE',
            borderRadius: '12px',
            _placeholder: {
              color: 'rgba(59, 72, 81, 0.6)',
            },
            _hover: {
              border: 'solid 2px #2F343D',
              paddingTop: '8px !important',
            },
            _focus: {
              backgroundColor: '#fff',
              border: 'solid 2px #2F343D',
              paddingTop: '8px !important',
            },
            paddingTop: '10px !important',
            lineHeight: '140%',
            fontSize: '14px',
          }),
        },
      },
      NumberInput: {
        baseStyle: (props) => ({
          // root: {
          //   color: 'error',
          // },
          // field: {
          //   color: 'success',
          // },
          stepper: {
            _first: {
              border: 'none',
              mr: '10px',
              _active: {
                bg: 'quinaryBackground',
              },
            },
            _last: {
              mt: '0',
              borderLeft: 'none',
              borderTop:
                props.colorMode === 'dark'
                  ? 'solid 1px rgba(255, 255, 255, 0.4)'
                  : 'solid 1px rgba(59, 72, 81, 0.4)',
              mr: '10px',
              _active: {
                bg: 'quinaryBackground',
              },
            },
          },
        }),
        variants: {
          filled: (props) => ({
            field:
              props.colorMode === 'dark'
                ? {
                    ...stylesControls,
                    ...stylesControlsForDarkMode,
                    pl: '16px',
                    _selection: {
                      bg: 'quinaryBackground',
                    },
                  }
                : {
                    ...stylesControls,
                    ...stylesControlsForLightBg,
                    pl: '16px',
                    _selection: {
                      bg: 'quinaryBackground',
                    },
                  },
          }),
          filledForDarkBg: (props) => ({
            field:
              props.colorMode === 'dark'
                ? {
                    ...stylesControls,
                    ...stylesControlsForDarkMode,
                    pl: '16px',
                    _selection: {
                      bg: 'transparent',
                    },
                  }
                : {
                    ...stylesControls,
                    ...stylesControlsForDarkBg,
                    pl: '16px',
                    _selection: {
                      bg: 'transparent',
                    },
                  },
          }),
        },
        sizes: {
          ...sizesControls,
        },
      },
      Form: {
        baseStyle: {
          requiredIndicator: {
            color: 'error',
            marginStart: 0,
          },
          helperText: {
            fontSize: '12px',
            mt: 0,
            lineHeight: '140%',
          },
        },
      },
      FormLabel: {
        baseStyle: {
          fontFamily: 'Inter Medium, sans-serif',
          fontSize: '14px',
          mb: '6px',
          lineHeight: '140%',
        },
        variants: {
          adminLabel: {
            fontFamily: 'Inter Medium, sans-serif',
            fontSize: '17px',
          },
          templateBuilderLabel: {
            fontFamily: 'Inter Regular, sans-serif',
            fontWeight: '400',
            fontSize: '16px',
          },
        },
      },
      FormError: {
        baseStyle: {
          text: {
            fontSize: '12px',
            mt: '7px',
            lineHeight: '140%',
          },
        },
      },
      Radio: {
        baseStyle: {
          label: {
            ml: '15px',
            transition: 'all 0.2s',
          },
        },
        variants: {
          white: (props) => ({
            control: {
              w: '20px',
              h: '20px',
              borderColor: 'white',
              transition: 'all 0.2s',
              _hover: {
                borderColor: 'white',
              },
              _checked: {
                borderColor: 'white',
                bg: 'transparent',
                _before: {
                  bg: 'white',
                },
                _hover: {
                  borderColor: 'white',
                  bg: 'transparent',
                },
              },
              _focus: {
                ring: 0,
              },
            },
          }),
        },
        sizes: {
          md: (props) => ({
            label: {
              fontSize: '14px',
              fontWeight: 400,
              color: props.colorMode === 'dark' ? '#DDDDDD' : 'secondaryDark08',
              _checked: {
                color: props.colorMode === 'dark' ? 'white' : 'primaryDark',
              },
              _hover: {
                color: props.colorMode === 'dark' ? 'white' : 'primaryDark',
              },
            },

            control: {
              w: '20px',
              h: '20px',
              borderColor:
                props.colorMode === 'dark' ? '#DDDDDD' : 'secondaryDark08',
              transition: 'all 0.2s',
              _hover: {
                borderColor: 'orangePrimary',
              },
              _checked: {
                borderColor: 'orangePrimary',
                bg: 'transparent',
                _before: {
                  bg: 'orangePrimary',
                },
                _hover: {
                  borderColor: 'orangePrimary',
                  bg: 'transparent',
                },
              },
              _focus: {
                ring: 0,
              },
            },
          }),
        },
      },
      Heading: {
        sizes: {
          md: {
            fontSize: '13px',
            lineHeight: '150%',
          },
          lg: {
            fontSize: '18px',
            lineHeight: '130%',
          },
          xl: {
            fontSize: '24px',
            lineHeight: '130%',
          },
          '2xl': {
            fontSize: '32px',
            lineHeight: '130%',
          },
        },
      },
      Container: {
        baseStyle: {
          // "maxW" should be used, instead of "maxWidth", so we can override it with props inside the code
          maxW: '100%',
        },
        variants: {
          bodyContent: {
            flex: '1 0 auto',
            padding: 0,
            display: 'flex',
          },
          apiContent: {
            flex: '1 0 auto',
            paddingTop: '0rem',
            paddingBottom: '0rem',
            display: 'flex',
          },
        },
      },
      Link: {
        baseStyle: {
          // cursor: 'crosshair',
          _hover: {
            // textDecoration: 'none',
            // color: 'secondaryDark'
          },
          _focus: {
            boxShadow: 'none',
          },
        },
        variants: {
          navbar: {
            color: 'white',
            fontSize: '1rem',
            fontFamily: 'Inter Bold, sans-serif',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            textTransform: 'capitalize',
            _hover: {
              textDecoration: 'none',
              color: 'orangePrimary',
            },
          },
          orange: {
            color: 'orangePrimary',
            _hover: {
              textDecoration: 'underline',
              color: 'orangePrimary',
            },
          },
          grey: {
            color: 'grey',
            _hover: {
              textDecoration: 'underline',
              color: 'grey',
            },
          },
          disabled: {
            color: 'grey',
            textDecoration: 'none',
            _hover: {
              textDecoration: 'none',
              color: 'grey',
            },
          },
        },
      },
      Switch: {
        baseStyle: (props) => ({
          track: {
            width: '32px',
            height: '14px',
            bg:
              props.colorMode === 'dark'
                ? 'rgba(221, 221, 221, 0.5)'
                : 'primaryBackground',
            _focus: {
              boxShadow: 'none',
            },
            _checked: {
              bg: 'orangePrimary',
            },
          },
          thumb: {
            width: '14px',
            height: '14px',
            _checked: {
              transform: 'translateX(18px)',
            },
          },
        }),
      },
      Button: {
        sizes: {
          xs: {
            height: '28px',
            lineHeight: '28px',
            fontSize: '14px',
          },
          sm: {
            height: '36px',
            lineHeight: '36px',
            fontSize: '14px',
          },

          md: {
            height: '45px',
            lineHeight: '24px',
            fontSize: '16px',
          },
        },
        variants: {
          gradient: {
            borderRadius: '5px',
            textTransform: 'none',
            px: '25px',
            bgColor: 'transparent',
            backgroundImage:
              'linear-gradient(180deg, #FF9554 0%, #FF4935 100%)',
            color: 'white',
            textStyle: 'bodyFamilyBold',
            transition: 'background-image 0.2s ease',
            _hover: {
              backgroundImage:
                'linear-gradient(90deg, rgba(137, 43, 34, 0.2) 0%, rgba(137, 43, 34, 0.2) 100%), linear-gradient(180deg, #FF9554 0%, #FF4935 100%)',
              _disabled: {
                backgroundImage: 'none',
                bg: '#C3C3C3',
              },
            },
            _active: {
              backgroundImage:
                'linear-gradient(90deg, rgba(137, 43, 34, 0.35) 0%, rgba(137, 43, 34, 0.35) 100%), linear-gradient(180deg, #FF9554 0%, #FF4935 100%)',
            },
            _disabled: {
              backgroundImage: 'none',
              bg: '#C3C3C3',
              color: 'white',
              cursor: 'not-allowed',
              pointerEvents: 'none',
            },
          },
          primary: {
            bg: 'orangePrimary',
            color: 'white',
            textStyle: 'bodyFamilyBold',
            fontSize: '14px',
            _hover: {
              opacity: 0.8,
            },
            _active: {
              bg: 'orangePrimary',
              opacity: 0.6,
            },
            _disabled: {
              bg: 'orangePrimary',
              color: 'white',
              opacity: 0.6,
              cursor: 'not-allowed',
              pointerEvents: 'none',
              _hover: {
                bg: 'orangePrimary',
                color: 'white',
                opacity: 0.6,
              },
            },
          },
          secondary: {
            position: 'relative',
            bg: 'white',
            fontSize: '14px',
            color: 'orangePrimary',

            _before: {
              content: '""',
              position: 'absolute',
              inset: 0,
              padding: '1.5px',
              borderRadius: 'inherit',
              background: 'linear-gradient(180deg, #FF9554 16%, #FF4935 100%)',
              WebkitMask:
                'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              pointerEvents: 'none',
            },

            '& > :not(.chakra-button__icon), & > :not(.chakra-button__icon) *':
              {
                bgClip: 'text',
                bgGradient: 'linear(to-b, #FF9554, #FF4935)',
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
              },

            '& .chakra-button__label, & .chakra-button__label *': {
              bgClip: 'text',
              bgGradient: 'linear(to-b, #FF9554, #FF4935)',
              color: 'transparent',
              WebkitTextFillColor: 'transparent',
            },

            '& .chakra-button__icon': {
              color: 'orangePrimary',
            },

            _loading: {
              color: 'orangePrimary',
              '& > :not(.chakra-button__icon), & > :not(.chakra-button__icon) *':
                {
                  color: 'orangePrimary',
                  bgClip: 'unset',
                  bgGradient: 'unset',
                  WebkitTextFillColor: 'unset',
                },
              '& .chakra-button__label, & .chakra-button__label *': {
                color: 'orangePrimary',
                bgClip: 'unset',
                bgGradient: 'unset',
                WebkitTextFillColor: 'unset',
              },
              '& .chakra-button__spinner, & .chakra-button__spinner *': {
                color: 'orangePrimary',
                borderColor: 'orangePrimary',
                bgClip: 'unset',
                bgGradient: 'unset',
                WebkitTextFillColor: 'unset',
              },
              '& .chakra-button__spinner *': {
                borderLeftColor: 'transparent',
              },
            },

            _hover: { bg: 'white', filter: 'brightness(0.98)' },
            _disabled: {
              opacity: 0.6,
            },
          },
          outline: {
            border: '1.5px solid',
            borderColor: 'orangePrimary',
            color: 'orangePrimary',
            fontSize: '14px',
            textStyle: 'bodyFamilyRegular',
            _hover: {
              bg: 'orangePrimary',
              color: 'white',
            },
          },
          danger: {
            bg: 'dangerRed',
            color: 'white',
            borderRadius: '5px',
            textStyle: 'bodyFamilyBold',
            fontSize: '14px',
            _hover: {
              bg: 'dangerRed',
              filter: 'brightness(0.92)',
              _disabled: { filter: 'none' },
            },
            _active: {
              bg: 'dangerRed',
              filter: 'brightness(0.85)',
            },
            _disabled: {
              bg: 'dangerRed',
              color: 'white',
              opacity: 0.6,
              cursor: 'not-allowed',
              pointerEvents: 'none',
            },
          },
          text: {
            bg: 'white',
            color: '#3A4A57',
            borderRadius: '5px',
            textStyle: 'bodyFamilyBold',
            fontSize: '14px',
            _hover: {
              bg: '#F8F8F8',
              _disabled: { bg: '#F0F0F0', color: '#C3C3C3' },
            },
            _active: {
              bg: '#F0F0F0',
            },
            _disabled: {
              bg: '#F0F0F0',
              color: '#C3C3C3',
              cursor: 'not-allowed',
              pointerEvents: 'none',
            },
          },
          secondaryFlat: {
            bg: 'white',
            color: '#3A4A57',
            border: '1.5px solid',
            borderColor: '#505E69',
            borderRadius: '5px',
            textStyle: 'bodyFamilyBold',
            fontSize: '14px',
            _hover: {
              bg: '#F2F2F2',
              borderColor: '#2F343D',
              color: '#2F343D',
              _disabled: {
                bg: '#F8F8F8',
                borderColor: '#AEAEAE',
                color: '#AEAEAE',
              },
            },
            _active: {
              bg: '#E6E6E6',
              borderColor: '#1D2B36',
              color: '#1D2B36',
            },
            _disabled: {
              bg: '#F8F8F8',
              borderColor: '#AEAEAE',
              color: '#AEAEAE',
              cursor: 'not-allowed',
              pointerEvents: 'none',
            },
          },
          destructive: {
            bg: '#FF0000',
            color: 'white',
            borderRadius: '5px',
            textStyle: 'bodyFamilyBold',
            fontSize: '14px',
            _hover: {
              bg: '#E60000',
              _disabled: { bg: '#C3C3C3' },
            },
            _active: {
              bg: '#CC0000',
            },
            _disabled: {
              bg: '#C3C3C3',
              color: 'white',
              cursor: 'not-allowed',
              pointerEvents: 'none',
            },
          },
          outlineDanger: {
            bg: 'transparent',
            border: '1.5px solid',
            borderColor: 'dangerRed',
            color: 'dangerRed',
            fontSize: '14px',
            textStyle: 'bodyFamilyRegular',
            _hover: {
              bg: 'dangerRed',
              color: 'white',
            },
          },
          outlineDark: {
            borderRadius: '5px',
            border: '1.5px solid',
            borderColor: '#3A4A57',
            bg: 'white',
            color: '#3A4A57',
            textStyle: 'bodyFamilyMedium',
            fontSize: '13px',
            _hover: { bg: '#F8F8F8' },
          },
          dark: {
            bg: '#3A4A56',
            color: 'white',
            textStyle: 'bodyFamilyRegular',
            fontSize: '14px',
            _hover: {
              bg: '#3A4A56',
              opacity: 0.8,
            },
            _disabled: {
              bg: 'white',
              border: '1.5px solid',
              borderColor: '#ADADAD',
              color: '#ADADAD',
              opacity: 0.6,
            },
          },
          grey: {
            bg: 'lightGrey2',
            color: 'primaryDark',
            textStyle: 'bodyFamilyRegular',
            fontWeight: '400',
            px: '25px',
          },
          qrCodeAction: {
            borderRadius: '10px',
            border: '2px solid',
            borderColor: 'lightGrey',
            bg: '#F8F8F8',
            color: 'black',
            textStyle: 'bodyFamilyRegular',
            fontSize: '14px',
            _hover: {
              bg: '#EEEEEE',
              '& path': { stroke: '#252525' },
            },
          },
          outlineRounded: {
            border: '2px solid',
            borderColor: 'orangePrimary',
            color: 'orangePrimary',
            fontSize: '16px',
            textStyle: 'bodyFamilyRegular',
            borderRadius: '25px',
            padding: '10px 25px',
          },
          gradientOutline: {
            border: '2.5px solid transparent',
            borderRadius: '25px',
            padding: '8px 15px',
            fontSize: '16px',
            textStyle: 'bodyFamilyRegular',
            backgroundImage:
              'linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(180deg, #FF9554 16.5%, #FF4935 100%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            _hover: { opacity: 0.8 },
          },
          white: (props) => ({
            ring: 0,
            backgroundColor:
              props.colorMode === 'dark' ? 'primaryDark' : 'white',
            color: props.colorMode === 'dark' ? 'white' : 'primaryDark',
            fontFamily: 'Gilroy Bold, sans-serif',
            fontWeight: '700',
            textTransform: 'uppercase',
            px: '40px',
            h: '45px',
            borderRadius: '25px',
            _focus: {
              ring: 0,
            },
            _hover: {
              backgroundColor:
                props.colorMode === 'dark'
                  ? 'primaryDark06'
                  : 'primaryBackground',
              _disabled: {
                backgroundColor:
                  props.colorMode === 'dark' ? 'primaryDark' : 'white',
              },
            },
            _active: {
              backgroundColor:
                props.colorMode === 'dark' ? 'primaryDark' : 'white',
            },
            _disabled: {
              opacity: 0.6,
            },
          }),
          white02: (props) => ({
            ring: 0,
            backgroundColor:
              props.colorMode === 'dark' ? 'secondaryDark' : 'white',
            color: props.colorMode === 'dark' ? 'white' : 'secondaryDark',
            fontFamily: 'Inter, sans-serif',
            fontWeight: '400',
            // textTransform: 'uppercase',
            px: '40px',
            h: '45px',
            borderRadius: '25px',
            _focus: {
              ring: 0,
            },
            _hover: {
              backgroundColor:
                props.colorMode === 'dark'
                  ? 'secondaryDark06'
                  : 'primaryBackground',
              _disabled: {
                backgroundColor:
                  props.colorMode === 'dark' ? 'secondaryDark' : 'white',
              },
            },
            _active: {
              backgroundColor:
                props.colorMode === 'dark' ? 'secondaryDark' : 'white',
            },
            _disabled: {
              opacity: 0.6,
            },
          }),
          link: {
            color: 'orangePrimary',
            textStyle: 'bodyFamilyRegular',
            fontWeight: '500',
            textDecoration: 'underline',
            textDecorationColor: 'orangePrimary',
            _focus: { ring: 0, color: 'orangePrimary' },
            _active: {
              color: 'orangePrimary',
            },
            _hover: {
              textDecoration: 'none',
              textDecorationColor: 'orangePrimary',
            },
          },
          mobileStepper: (props) => ({
            ring: 0,
            backgroundColor:
              props.colorMode === 'dark' ? 'secondaryDark' : 'white',
            color: props.colorMode === 'dark' ? 'white' : 'secondaryDark',
            h: '45px',
            borderRadius: '10px',
            _focus: {
              ring: 0,
            },
            _hover: {
              backgroundColor:
                props.colorMode === 'dark'
                  ? 'secondaryDark06'
                  : 'primaryBackground',
              _disabled: {
                backgroundColor:
                  props.colorMode === 'dark' ? 'secondaryDark' : 'white',
              },
            },
            _active: {
              backgroundColor:
                props.colorMode === 'dark' ? 'secondaryDark' : 'white',
            },
            _disabled: {
              opacity: 0.6,
            },
          }),
          gPay: {
            ring: 0,
            backgroundColor: '#000',
            color: '#fff',
            h: '60px',
            width: '280px',
            borderRadius: '30px',
            _focus: {
              ring: 0,
            },
            _hover: {
              opacity: '0.7',
            },
          },
          applePay: {
            ring: 0,
            backgroundColor: '#000',
            color: '#fff',
            borderRadius: '15px',
            _focus: {
              ring: 0,
            },
            _hover: {
              opacity: '0.7',
            },
          },
          myAccount: (props) => ({
            ring: 0,
            backgroundColor: 'transparent',
            color: props.colorMode === 'dark' ? 'white' : 'primaryDark',
            fontFamily: 'Gilroy Bold, sans-serif',
            fontSize: '14px',
            h: '30px',
            border: 'none',
            _focus: {
              ring: 0,
            },
          }),
          dashTime: (props) => ({
            ring: 0,
            backgroundColor: props.colorMode === 'dark' ? '#2F343D' : 'white',
            color: props.colorMode === 'dark' ? 'white' : 'primaryDark',
            fontWeight: '400',
            px: '16px',
            h: '18px',
            borderRadius: '25px',
            _focus: {
              ring: 0,
            },
            _hover: {
              border:
                props.colorMode === 'dark'
                  ? '2px solid #EFEFEF'
                  : '2px solid #000000',
            },
          }),
          previewStepper: {
            ring: 0,
            backgroundColor: 'orangePrimary',
            color: 'white',
            px: '40px',
            h: '45px',
            borderRadius: '25px',
            _focus: {
              ring: 0,
            },
            _hover: {
              backgroundColor: 'orangePrimary',
              opacity: 0.7,
              _disabled: {
                backgroundColor: 'orangePrimary',
              },
            },
            _active: {
              backgroundColor: 'orangePrimary',
            },
            _disabled: {
              opacity: 0.6,
            },
          },
        },
      },
      Drawer: {
        sizes: {
          sm: {
            dialog: { maxW: '250px' },
          },
        },
      },
      CloseButton: {
        baseStyle: {
          _focus: {
            boxShadow: 'none',
          },
        },
      },
      Divider: {
        baseStyle: {
          opacity: 1,
        },
      },
      Modal: {
        sizes: {
          xl: (props) => ({
            dialog: {
              maxW: '560px',
              borderRadius: '15px',
              bg: props.colorMode === 'dark' ? 'primaryDark' : 'white',
            },
            header: {
              padding: '24px 48px 0 24px',
            },
            body: {
              padding: '24px',
            },
            footer: {
              padding: '0 24px 24px',
            },
          }),

          l: (props) => ({
            dialog: {
              maxW: '560px',
              borderRadius: '15px',
              bg: props.colorMode === 'dark' ? 'primaryDark' : 'white',
            },
            header: {
              padding: '24px 48px 0 24px',
            },
            body: {
              padding: '24px 24px 10px',
            },
            footer: {
              padding: '24px',
            },
          }),
        },
      },
      Menu: {
        baseStyle: {
          list: {},
          item: {},
          divider: {},
        },
        variants: {
          myAccount: {
            list: {
              p: '0',
              borderRadius: '6px',
              border: 'none',
              minW: 'auto',
              boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.1)',
            },
            item: {
              fontSize: '14px',
              textStyle: 'bodyFamilyMedium',
              _hover: { bg: 'quinaryBackground' },
              p: 3,
              _focus: {
                bg: 'quinaryBackground',
              },
            },
          },
        },
      },
      Breadcrumb: {
        baseStyle: {
          link: {
            // color: 'red',
          },
        },
      },
      Table: {
        // The rest of the styling for Table component is within react-table columns
        baseStyle: (props) => ({
          // tr: {},
          th: {
            fontSize: '12px',
            fontFamily: 'Inter Medium, sans-serif',
            textTransform: 'uppercase',
            // textAlign: 'start',
            letterSpacing: 'wide',
          },
          td: {
            fontSize: '14px',
            textAlign: 'start',
            '&.modeTdColor': {
              color: props.colorMode === 'dark' ? 'white' : 'primaryDark',
            },
          },
        }),
        variants: {
          simple: (props) => ({
            tbody: {
              tr: {
                bg: props.colorMode === 'dark' ? 'transparent' : 'white',
                transition: '0.3s',
                h: '63px',
                cursor: 'pointer',
                _hover: {
                  bg:
                    props.colorMode === 'dark'
                      ? 'primaryDark'
                      : 'rgba(221, 221, 221, 0.5)',
                },
              },
            },
            th: {
              color: props.colorMode === 'dark' ? '#DDDDDD' : 'secondaryDark08',
              borderColor:
                props.colorMode === 'dark'
                  ? 'primaryDark'
                  : 'primaryBackground',
            },
            td: {
              color: props.colorMode === 'dark' ? '#DDDDDD' : 'secondaryDark08',
              borderColor:
                props.colorMode === 'dark'
                  ? 'primaryDark'
                  : 'primaryBackground',
            },
          }),
        },
        sizes: {
          md: {
            th: {
              // paddingLeft: 0,
              // paddingRight: '12px',
              px: '10px',
              py: '9px',
            },
            td: {
              px: '10px',
              py: '6px',
            },
          },
        },
      },
      Badge: {
        baseStyle: {
          px: '10px',
          py: '6px',
          textTransform: 'none',
          borderRadius: '12px',
          lineHeight: '100%',
          fontWeight: '400',
          color: 'white',
          bg: 'orangePrimary',
        },
        variants: {
          white: {
            bg: 'white',
            color: 'secondaryDark',
          },
          issued: {
            bg: '#0DB2F9',
            color: 'white',
          },
          active: {
            bg: 'success',
            color: 'white',
          },
          pending: {
            bg: 'warning',
            color: 'white',
          },
          removed: {
            bg: 'removed',
            color: 'white',
          },
          error: {
            bg: 'error',
            color: 'white',
          },
          error02: {
            bg: '#E53E3E',
            color: '#fff',
          },
          disabled: {
            bg: '#9AA0A4',
            color: 'primaryDark',
            border: 'none',
          },
          groupTag: {
            bg: 'rgba(80, 94, 105, 0.2)',
            color: '#505E69',
            border: '1px solid #505E69',
            borderRadius: '5px',
            px: '8px',
            py: '5px',
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: '400',
            lineHeight: '1.4',
          },
        },
      },
      Checkbox: {
        variants: {
          primary: () => ({
            control: {
              w: '18px',
              h: '18px',
              borderRadius: '5px',
              borderColor: 'lightGrey',
              _checked: {
                bg: 'orangePrimary',
                borderColor: 'orangePrimary',
                color: 'white',
                _hover: {
                  bg: 'orangePrimary',
                  borderColor: 'orangePrimary',
                },
              },
            },
            label: {
              fontFamily: 'Inter Regular, sans-serif',
              fontSize: '14px',
              ml: 2,
              position: 'relative',
            },
          }),
          formPreview: () => ({
            control: {
              w: '12px',
              h: '12px',
              borderRadius: '4px',
              borderColor: 'darkGrey',
              _checked: {
                bg: 'orangePrimary',
                borderColor: 'orangePrimary',
                color: 'white',
                _hover: {
                  bg: 'orangePrimary',
                  borderColor: 'orangePrimary',
                },
              },
            },
          }),
        },
        baseStyle: (props) => ({
          control: {
            borderRadius: '2px',
            borderColor:
              props.colorMode === 'dark' ? '#DDDDDD' : 'secondaryDark08',
            _checked: {
              bg: 'orangePrimary',
              borderColor: 'orangePrimary',
              color: 'white',
              _hover: {
                bg: 'orangePrimary',
                borderColor: 'orangePrimary',
              },
              _disabled: {
                bg:
                  props.colorMode === 'dark' ? '#DDDDDD' : 'primaryBackground',
                borderColor:
                  props.colorMode === 'dark' ? '#DDDDDD' : 'primaryBackground',
                color:
                  props.colorMode === 'dark'
                    ? 'secondaryDark06'
                    : 'secondaryDark06',
              },
            },
            _indeterminate: {
              bg: 'orangePrimary',
              borderColor: 'orangePrimary',
              color: 'white',
            },
            _focus: {
              boxShadow: 'none',
              ring: 0,
            },
            _disabled: {
              bg: props.colorMode === 'dark' ? '#DDDDDD' : 'primaryBackground',
              borderColor:
                props.colorMode === 'dark' ? '#DDDDDD' : 'primaryBackground',
              color:
                props.colorMode === 'dark'
                  ? 'secondaryDark06'
                  : 'secondaryDark06',
            },
          },
          label: {
            fontFamily: 'Inter Medium, sans-serif',
            color: props.colorMode === 'dark' ? 'white' : 'secondaryDark',
            ml: 3,
            position: 'relative',
            top: '1px',
          },
        }),
        sizes: {
          md: {
            control: {
              w: '14px',
              h: '14px',
            },
            label: { fontSize: '16px' },
            icon: {
              fontSize: '8px',
            },
          },
        },
      },
      Tabs: {
        variants: {
          line: (props) => ({
            tablist: {
              borderColor:
                props.colorMode === 'dark'
                  ? 'primaryDark'
                  : 'primaryBackground',
            },
            tab: {
              color: props.colorMode === 'dark' ? '#DDDDDD' : 'secondaryDark08',
              _selected: {
                color: props.colorMode === 'dark' ? 'white' : 'secondaryDark',
                borderColor: 'orangePrimary',
              },
              _focus: {
                boxShadow: 'none',
              },
              _active: {
                background: 'none',
              },
              _hover: {
                color: props.colorMode === 'dark' ? 'white' : 'secondaryDark',
              },
            },
          }),
        },
      },
      Slider: {
        baseStyle: {
          filledTrack: { bg: 'orangePrimary' },
        },
        sizes: {
          md: {
            thumb: {
              w: '14px',
              h: '14px',
              bg: 'gradientBrand',
              _focus: {
                boxShadow: 'none',
              },
            },
            track: {
              bg: 'primaryBackground',
              h: '2px',
            },
          },
        },
      },
      Spinner: {
        baseStyle: {
          width: '52px',
          height: '52px',
          thickness: '4px',
        },
        sizes: {
          md: {
            color: 'orangePrimary',
            thickness: '4px',
          },
        },
      },
      Stepper: {
        baseStyle: (props) => ({
          colorScheme: 'primaryBackground',
          indicator: {
            borderColor: props.colorMode === 'dark' ? '#DDDDDD' : '#9F9F9F',
          },
        }),
      },
      Box: {},
      Progress: {
        baseStyle: {
          filledTrack: {
            bg: 'orangePrimary',
          },
          track: {
            bg: 'lightGrey',
          },
        },
        variants: {
          gradient: {
            filledTrack: {
              bg: 'linear-gradient(180deg, #FF9554 16%, #FF4935 100%)',
            },
          },
          danger: {
            filledTrack: {
              bg: '#FF0F0F',
            },
          },
        },
      },
    },
    breakpoints: {
      xl2: '83.5em', // 1336px
      xl3: '90em', // 1440px
    },
  },
  withDefaultVariant({
    variant: 'filled',
    components: ['Input', 'Select', 'Textarea', 'NumberInput'], // colide with components array from other withDef...
  }),
  withDefaultVariant({
    variant: 'gradient',
    components: ['Button'], // colide with components array from other withDef...
  })
);
